import { F0Alert, F0Box, F0Heading, F0Text } from '@factorialco/f0-react'
import { useNavigate } from 'react-router-dom'

import i18n from 'modules/core/lib/i18n'
import { toCents } from 'modules/core/lib/formatCurrencyUtils'
import { amountWithCurrency } from 'modules/finance/lib/budgets'

import type { TrainingBudgetMovement } from 'modules/trainings/components/Budgets/TrainingResourceDataCollection/index'

interface Props {
  item: TrainingBudgetMovement
  locale: string
  groupUrl: string
}

export const CostTab = ({ item, locale, groupUrl }: Props) => {
  const navigate = useNavigate()
  const resource = item.budgetedResource
  const currency = resource.currency ?? item.sourceCurrency

  const formatAmount = (amount: string | number | null | undefined) =>
    amount != null ? amountWithCurrency(toCents(Number(amount)), currency, locale) : '-'

  return (
    <F0Box display="flex" flexDirection="column" gap="xl">
      <F0Box>
        <F0Heading
          content={i18n.t('budgets.detail_training_budget.sidepanel.cost_breakdown_title')}
        />
      </F0Box>
      <F0Box border="default" borderRadius="md" flexDirection="column" borderColor="secondary">
        <CostRow
          title={i18n.t('budgets.detail_training_budget.sidepanel.direct_cost_desc')}
          description={i18n.t('budgets.detail_training_budget.sidepanel.direct_cost_hint')}
          amount={formatAmount(resource.cost)}
        />
        <CostRow
          title={i18n.t('budgets.detail_training_budget.sidepanel.indirect_cost_desc')}
          description={i18n.t('budgets.detail_training_budget.sidepanel.indirect_cost_hint')}
          amount={formatAmount(resource.indirectCost)}
          withDivider
        />
        <CostRow
          title={i18n.t('budgets.detail_training_budget.sidepanel.salary_cost_desc')}
          description={i18n.t('budgets.detail_training_budget.sidepanel.salary_cost_hint')}
          amount={formatAmount(resource.salaryCost)}
          withDivider
        />
      </F0Box>
      <F0Box paddingX="md" borderColor="secondary" borderBottom="default" />
      <F0Box border="default" borderRadius="md" borderColor="secondary">
        <CostRow
          title={i18n.t('budgets.detail_training_budget.sidepanel.total_cost_desc')}
          description={i18n.t('budgets.detail_training_budget.sidepanel.total_cost_hint')}
          amount={formatAmount(resource.grossCost)}
          bold
        />
      </F0Box>
      <F0Box>
        <F0Box paddingTop="2xl" width="full">
          <F0Alert
            action={{
              label: i18n.t('budgets.detail_training_budget.sidepanel.go_to_group'),
              onClick: () => navigate(groupUrl),
            }}
            variant="info"
            title={i18n.t('budgets.detail_training_budget.sidepanel.footer_note')}
            description=""
          />
        </F0Box>
      </F0Box>
    </F0Box>
  )
}

const CostRow = ({
  title,
  description,
  amount,
  bold = false,
  withDivider = false,
}: {
  title: string
  description: string
  amount: string
  bold?: boolean
  withDivider?: boolean
}) => (
  <F0Box
    display="flex"
    flexDirection="row"
    justifyContent="between"
    alignItems="center"
    padding="md"
    borderTop={withDivider ? 'default' : undefined}
    borderColor={withDivider ? 'secondary' : undefined}
  >
    <F0Box flexDirection="column" gap="xs">
      <F0Text variant="label" content={title} />
      <F0Text variant="description" content={description} ellipsis />
    </F0Box>
    <F0Text variant={bold ? 'label' : 'body'} content={amount} />
  </F0Box>
)
